import { loadStripe, Stripe } from '@stripe/stripe-js';
import { supabase } from './supabase';
import { TokenPackage } from '../types/auth';

// Load Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Stripe configuration
const STRIPE_CONFIG = {
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
  // Note: Secret key should NEVER be used in frontend - only for backend API calls
  currency: 'usd',
  country: 'US'
};

if (!STRIPE_CONFIG.publishableKey) {
  console.error('❌ REACT_APP_STRIPE_PUBLISHABLE_KEY is not set in environment variables');
}

export interface PaymentIntent {
  clientSecret: string;
  amount: number;
  currency: string;
  id: string;
  status: string;
}

export interface PaymentResult {
  success: boolean;
  paymentIntent?: any;
  error?: string;
}

export interface PaymentHistoryItem {
  payment_intent_id: string;
  amount_cents: number;
  status: string;
  tokens_purchased: number | null;
  package_name: string | null;
  created_at: string;
}

export const stripeService = {
  // Get Stripe instance
  async getStripe(): Promise<Stripe | null> {
    return await stripePromise;
  },

  // Create payment intent with Supabase Edge Function
  async createPaymentIntent(tokenPackage: TokenPackage, userId: string): Promise<PaymentIntent> {
    try {
      console.log('🔧 Creating payment intent for package:', tokenPackage.name);
      
      // Try to call Supabase Edge Function first
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (supabaseUrl) {
        try {
          console.log('🔧 Attempting to create payment intent via Supabase Edge Function...');
          const response = await fetch(`${supabaseUrl}/functions/v1/create-payment-intent`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              tokenPackageId: tokenPackage.id,
              userId: userId,
              amountCents: tokenPackage.price_cents,
              currency: STRIPE_CONFIG.currency
            })
          });

          if (response.ok) {
            const data = await response.json();
            console.log('✅ Payment intent created via Edge Function:', data.payment_intent_id);
            
            return {
              clientSecret: data.client_secret,
              amount: tokenPackage.price_cents,
              currency: STRIPE_CONFIG.currency,
              id: data.payment_intent_id,
              status: data.status
            };
          } else {
            const errorData = await response.json();
            console.warn('⚠️ Edge Function failed, falling back to test mode:', errorData);
          }
        } catch (edgeFunctionError) {
          console.warn('⚠️ Edge Function not available, falling back to test mode:', edgeFunctionError);
        }
      }
      
      // Fallback to test payment intent if Edge Function fails
      console.log('🔧 Creating test payment intent as fallback...');
      const testIntent = await this.createTestPaymentIntent(tokenPackage, userId);
      
      return {
        clientSecret: testIntent.client_secret,
        amount: tokenPackage.price_cents,
        currency: STRIPE_CONFIG.currency,
        id: testIntent.id,
        status: testIntent.status
      };
    } catch (error) {
      console.error('💥 Error creating payment intent:', error);
      throw new Error('Failed to create payment intent');
    }
  },

  // Process payment with real Stripe Elements
  async processPayment(
    stripe: Stripe | null,
    elements: any,
    clientSecret: string
  ): Promise<PaymentResult> {
    try {
      console.log('💳 Processing payment...');
      
      // Check if this is a test payment intent (contains 'secret_')
      if (clientSecret.includes('secret_')) {
        console.log('🧪 Processing test payment...');
        
        // Simulate payment processing delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Create a mock successful payment result
        const mockPaymentIntent = {
          id: clientSecret.split('_secret_')[0],
          status: 'succeeded',
          amount: 0, // Will be set by the calling code
          metadata: {
            user_id: 'test-user',
            package_id: 'test-package',
            tokens: '50',
            package_name: 'Test Package'
          }
        };
        
        console.log('✅ Test payment succeeded:', mockPaymentIntent.id);
        
        return {
          success: true,
          paymentIntent: mockPaymentIntent
        };
      }
      
      // Real payment processing - requires stripe and elements
      if (!stripe || !elements) {
        return {
          success: false,
          error: 'Payment system not initialized'
        };
      }
      
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
        },
        redirect: 'if_required'
      });

      if (error) {
        console.error('❌ Payment failed:', error.message);
        
        // Handle specific Stripe error types
        let errorMessage = error.message || 'Payment failed';
        
        if (error.type === 'card_error') {
          switch (error.code) {
            case 'card_declined':
              errorMessage = 'Your card was declined. Please try a different payment method.';
              break;
            case 'insufficient_funds':
              errorMessage = 'Insufficient funds. Please try a different card.';
              break;
            case 'expired_card':
              errorMessage = 'Your card has expired. Please use a valid card.';
              break;
            case 'incorrect_cvc':
              errorMessage = 'Your card\'s security code is incorrect.';
              break;
            case 'processing_error':
              errorMessage = 'An error occurred while processing your card. Please try again.';
              break;
            default:
              errorMessage = `Card error: ${error.message}`;
          }
        }
        
        return {
          success: false,
          error: errorMessage
        };
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('✅ Payment succeeded:', paymentIntent.id);
        
        // Record successful payment in Supabase
        await this.recordSuccessfulPayment(paymentIntent);
        
        return {
          success: true,
          paymentIntent
        };
      }

      return {
        success: false,
        error: 'Payment was not completed'
      };
    } catch (error) {
      console.error('💥 Payment processing error:', error);
      return {
        success: false,
        error: 'An unexpected error occurred during payment'
      };
    }
  },

  // Record successful payment in Supabase
  async recordSuccessfulPayment(paymentIntent: any): Promise<void> {
    try {
      console.log('📝 Recording successful payment:', paymentIntent.id);
      
      const { error } = await supabase.rpc('record_payment_success', {
        p_stripe_payment_intent_id: paymentIntent.id,
        p_user_id: paymentIntent.metadata?.user_id,
        p_token_package_id: paymentIntent.metadata?.package_id,
        p_amount_cents: paymentIntent.amount,
        p_tokens_purchased: parseInt(paymentIntent.metadata?.tokens || '0'),
        p_stripe_charge_id: paymentIntent.latest_charge,
        p_payment_method: paymentIntent.payment_method_types?.[0] || 'card'
      });

      if (error) {
        console.error('❌ Error recording payment success:', error);
        throw error;
      }

      console.log('✅ Payment success recorded in database');

      // Create invoice for successful payment
      try {
        const { invoiceService } = await import('./invoiceService');
        const invoiceResult = await invoiceService.createInvoice(
          paymentIntent.id,
          paymentIntent.metadata?.user_id,
          paymentIntent.metadata?.package_id,
          paymentIntent.amount
        );

        if (invoiceResult.success) {
          console.log('✅ Invoice created successfully:', invoiceResult.invoice?.invoice_id);
        } else {
          console.warn('⚠️ Invoice creation failed:', invoiceResult.error);
        }
      } catch (invoiceError) {
        console.warn('⚠️ Invoice creation error:', invoiceError);
        // Don't fail the payment if invoice creation fails
      }
    } catch (error) {
      console.error('💥 Error recording payment success:', error);
      // Don't throw here as the payment was successful, just log the error
    }
  },

  // Get user's payment history from token transactions
  async getPaymentHistory(userId: string): Promise<PaymentHistoryItem[]> {
    try {
      console.log('📊 Fetching payment history for user:', userId);
      
      const { data, error } = await supabase
        .from('token_transactions')
        .select('*')
        .eq('user_id', userId)
        .eq('transaction_type', 'purchase')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching payment history:', error);
        throw error;
      }

      console.log('✅ Payment history data received:', data);
      
      // Transform token transactions to payment history format
      const paymentHistory = (data || []).map(transaction => ({
        payment_intent_id: transaction.reference_id || transaction.id,
        amount_cents: 0, // We don't store amount in token transactions
        status: 'succeeded', // All recorded purchases are successful
        tokens_purchased: transaction.amount,
        package_name: transaction.description || 'Token Purchase',
        created_at: transaction.created_at
      }));

      return paymentHistory;
    } catch (error) {
      console.error('💥 Error fetching payment history:', error);
      return [];
    }
  },

  // Get payment intent status
  async getPaymentIntentStatus(paymentIntentId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('stripe_payment_intents')
        .select('status')
        .eq('stripe_payment_intent_id', paymentIntentId)
        .single();

      if (error) {
        console.error('❌ Error fetching payment intent status:', error);
        return null;
      }

      return data?.status || null;
    } catch (error) {
      console.error('💥 Error fetching payment intent status:', error);
      return null;
    }
  },

  // Format amount for display
  formatAmount(amountInCents: number): string {
    return (amountInCents / 100).toFixed(2);
  },

  // Validate Stripe configuration
  isConfigured(): boolean {
    return !!STRIPE_CONFIG.publishableKey;
  },

  // Get configuration info
  getConfig() {
    return {
      isConfigured: this.isConfigured(),
      currency: STRIPE_CONFIG.currency,
      country: STRIPE_CONFIG.country,
      hasPublishableKey: !!STRIPE_CONFIG.publishableKey
    };
  },

  // Test mode utilities
  async createTestPaymentIntent(tokenPackage: TokenPackage, userId: string) {
    console.log('🧪 Creating test payment intent...');
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Create a realistic payment intent structure that works with Stripe Elements
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 9);
    
    const mockPaymentIntent = {
      id: `pi_${randomId}${timestamp}`,
      client_secret: `pi_${randomId}${timestamp}_secret_${Math.random().toString(36).substr(2, 9)}`,
      amount: tokenPackage.price_cents,
      currency: STRIPE_CONFIG.currency,
      status: 'requires_payment_method',
      metadata: {
        user_id: userId,
        package_id: tokenPackage.id,
        tokens: tokenPackage.tokens.toString(),
        package_name: tokenPackage.name
      }
    };

    console.log('🔄 Test payment intent created:', mockPaymentIntent.id);
    return mockPaymentIntent;
  }
}; 